/**
 * SupportWidget — SCK-549 PR-7.
 *
 * Floating chat entry point that mounts on every authenticated console
 * page. Collapsed state is a brand-orange FAB in the bottom-right;
 * expanded state is a compact popover with a PageHeader-style card
 * header, the message stream, and a composer. The same component
 * (in "full" variant) also drives the dedicated /support page in PR-8.
 *
 * UX contract (per feedback_ui_utility_and_class):
 *  - PageHeader-style card header (title + subtitle + hover-reveal
 *    action row) — no bare divs
 *  - EmptyState placeholder before the first message
 *  - Skeleton streaming shimmer while a response is streaming (see
 *    ChatMessage)
 *  - Hover-reveal minimize + close controls in the header — always
 *    keyboard-reachable, opacity ramps up on hover so the resting
 *    surface stays clean
 *  - Keyboard shortcuts:
 *      Ctrl/Cmd + / — toggle the widget (open ↔ collapsed)
 *      Esc        — collapse the open widget
 *      Enter      — send
 *      Shift+Enter — newline in composer
 *
 * Behaviour:
 *  - Feature-flagged via REACT_APP_SUPPORT_WIDGET_ENABLED. When the
 *    flag is off, the whole tree renders null so PR-7 can ship dark.
 *  - Message history is session-local (in-memory + hook state). Draft
 *    text (composer buffer) is persisted per-viewer under
 *    DRAFT_STORAGE_KEY so a stray tab-close doesn't lose it.
 *  - Never elevates: the hook posts with getToken() and nothing else.
 *  - No LLM call is made from the widget — the SSE proxy in coord
 *    (PR-2) is the sole path to the Daari LLM Gateway.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import { getEnv } from "../../helpers/env";
import EmptyState from "../../components/Directory/primitives/EmptyState";
import ChatMessage from "./ChatMessage";
import useSupportChat from "./useSupportChat";
import {
  COPY,
  DRAFT_STORAGE_KEY,
  SUPPORT_WIDGET_FLAG,
} from "./constants";

/**
 * The widget renders in two variants:
 *   - "floating" (default): FAB + popover, mounted in the app shell.
 *   - "full"             : header + stream + composer only, no FAB,
 *                          no absolute positioning — for the /support
 *                          dedicated page (PR-8 will consume this).
 */
export default function SupportWidget({ variant = "floating" }) {
  const flagRaw = getEnv(SUPPORT_WIDGET_FLAG);
  const enabled =
    flagRaw === true ||
    flagRaw === "true" ||
    flagRaw === "1" ||
    flagRaw === 1;

  // Full-page variant renders even without the flag — the /support
  // route in PR-8 is its own gate. Floating variant hides on flag-off.
  if (!enabled && variant === "floating") return null;

  return <SupportWidgetInner variant={variant} />;
}

function SupportWidgetInner({ variant }) {
  const isFull = variant === "full";
  const [open, setOpen] = useState(isFull); // full variant is always "open"
  const [draft, setDraft] = useState(() => {
    try {
      return window.localStorage.getItem(DRAFT_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  const { messages, isStreaming, sendMessage, reset } = useSupportChat();
  const streamEndRef = useRef(null);
  const composerRef = useRef(null);

  // Persist draft per-viewer (SOC 2: no message content persisted;
  // draft is the composer buffer, not sent history).
  useEffect(() => {
    try {
      if (draft) window.localStorage.setItem(DRAFT_STORAGE_KEY, draft);
      else window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      /* private-mode; ignore */
    }
  }, [draft]);

  // Auto-scroll on new messages / streaming updates.
  useEffect(() => {
    if (!open) return;
    // jsdom in tests + some legacy embed contexts lack scrollIntoView;
    // treat it as a nice-to-have rather than a hard requirement.
    if (streamEndRef.current && typeof streamEndRef.current.scrollIntoView === "function") {
      streamEndRef.current.scrollIntoView({ block: "end" });
    }
  }, [messages, open]);

  // Focus composer when the popover opens.
  useEffect(() => {
    if (open && composerRef.current) {
      composerRef.current.focus();
    }
  }, [open]);

  // Global keyboard shortcut — Ctrl/Cmd + / toggles the floating
  // variant. No-op in the full variant (already visible).
  useEffect(() => {
    if (isFull) return;
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === "/") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFull, open]);

  const handleSubmit = useCallback(() => {
    const text = draft.trim();
    if (!text || isStreaming) return;
    sendMessage(text);
    setDraft("");
    try {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [draft, isStreaming, sendMessage]);

  const onComposerKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Only one dismiss control: Close. An earlier revision had a
  // Minimize button next to it, but both called setOpen(false) — two
  // adjacent controls with different names and identical behavior read
  // as a comprehension bug. A real "keep the draft, park the widget"
  // affordance is future work; for now, Close + the draft-in-localStorage
  // persistence already give a "come back to it" story.
  const header = (
    <HeaderRow
      onClose={isFull ? null : () => setOpen(false)}
      onReset={messages.length > 0 && !isStreaming ? reset : null}
    />
  );

  const stream = (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 2,
        py: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
      data-testid="support-stream"
    >
      {messages.length === 0 ? (
        <EmptyState
          icon={<SupportAgentIcon />}
          title={COPY.emptyTitle}
          description={COPY.emptyDescription}
          variant="no-match" /* no CTA — the composer IS the CTA */
        />
      ) : (
        messages.map((m) => <ChatMessage key={m.id} message={m} />)
      )}
      <div ref={streamEndRef} />
    </Box>
  );

  const composer = (
    <Box sx={{ p: 1.5, pt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          inputRef={composerRef}
          multiline
          minRows={1}
          maxRows={5}
          fullWidth
          size="small"
          placeholder={COPY.placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onComposerKey}
          disabled={isStreaming}
          inputProps={{
            "aria-label": "Support chat message",
            maxLength: 4000,
          }}
        />
        <Button
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={!draft.trim() || isStreaming}
          sx={{ flexShrink: 0 }}
        >
          {COPY.send}
        </Button>
      </Stack>
    </Box>
  );

  // ---- Render: full page variant ------------------------------------
  if (isFull) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 480,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
          overflow: "hidden",
        }}
      >
        {header}
        <Divider />
        {stream}
        <Divider />
        {composer}
      </Paper>
    );
  }

  // ---- Render: floating widget --------------------------------------
  return (
    <>
      <Zoom in={!open}>
        <Tooltip title={COPY.fabAria} placement="left">
          <Fab
            color="primary"
            aria-label={COPY.fabAria}
            onClick={() => setOpen(true)}
            sx={{
              position: "fixed",
              right: 24,
              bottom: 24,
              zIndex: (t) => t.zIndex.speedDial,
            }}
          >
            <ChatBubbleOutlineIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      {open && (
        <Paper
          elevation={12}
          role="dialog"
          aria-label="Support chat"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            width: { xs: "calc(100vw - 48px)", sm: 400 },
            height: { xs: "calc(100dvh - 96px)", sm: 560 },
            maxHeight: "calc(100dvh - 48px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
            zIndex: (t) => t.zIndex.modal,
          }}
        >
          {header}
          <Divider />
          {stream}
          <Divider />
          {composer}
        </Paper>
      )}
    </>
  );
}

/**
 * HeaderRow — mirrors the PageHeader primitive's shape (title +
 * subtitle stack on the left, action node on the right) but sized for
 * a popover card. Minimize/close/reset buttons live in a hover-reveal
 * group: full-opacity on hover or focus-within, dimmed at rest.
 */
function HeaderRow({ onClose, onReset }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component="header"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        px: 2,
        pt: 1.25,
        pb: 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: "background.paper",
      }}
    >
      <SupportAgentIcon color="primary" fontSize="small" />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{ fontWeight: 600, lineHeight: 1.2 }}
        >
          {COPY.headerTitle}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.2 }}
        >
          {COPY.headerSubtitle}
        </Typography>
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          opacity: hovered ? 1 : 0.55,
          transition: "opacity 120ms ease",
          "&:focus-within": { opacity: 1 },
        }}
      >
        {onReset && (
          <Tooltip title="Start new conversation">
            <IconButton
              size="small"
              onClick={onReset}
              aria-label="Reset support conversation"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onClose && (
          <Tooltip title={COPY.close}>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label={COPY.close}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
